import React from "react";
import {
  faAngleLeft,
  faAngleRight,
  faAngleDoubleRight,
  faAngleDoubleLeft,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useLocation } from "react-router-dom";
import buildUrlParams from "./buildUrlParams";
import pagerHelper from "./pagerHelper";

const Pager = ({
  total,
  show,
  page,
  itemsPerPage,
}: {
  page: string;
  total: number | "...";
  itemsPerPage: number;
  show: boolean;
}) => {
  const { size, lastPage, end } = pagerHelper({
    total,
    itemsPerPage,
    page,
  });

  const { search, pathname } = useLocation();

  const { params, nextUrlParams } = buildUrlParams(search, {
    page: `${size + 1}`,
  });

  // eslint-disable-next-line
  const { page: urlPage, ...rest } = params;

  const firstUrlParams = new URLSearchParams({
    ...rest,
  }).toString();
  const prevUrlParams = new URLSearchParams({
    ...params,
    page: `${size - 1}`,
  }).toString();

  const prevPageUrl =
    size === 2
      ? `${pathname}?${firstUrlParams}`
      : `${pathname}?${prevUrlParams}`;

  const startEnd = size;

  const lastUrlParams = new URLSearchParams({
    ...params,
    page: `${lastPage}`,
  }).toString();

  const scrollTop = () => {
    if (show) {
      setTimeout(() => {
        window.scrollTo(0, 0);
      });
    }
  };

  React.useEffect(scrollTop, [page]);

  return show ? (
    <div className="float-right mt-3">
      <ul className="list-inline">
        <li className="list-inline-item">
          <div className="pager">
            {size === 1 ? (
              <span className="text-muted">
                <FontAwesomeIcon icon={faAngleDoubleLeft} /> First
              </span>
            ) : (
              <Link
                data-testid="nextPageButton"
                to={`${pathname}?${firstUrlParams}`}
              >
                <FontAwesomeIcon icon={faAngleDoubleLeft} /> First
              </Link>
            )}
          </div>
        </li>
        <li className="list-inline-item">
          {size === 1 ? (
            <div className="pager">
              <span
                className="text-muted"
                data-testid="disabledPreviousPageButton"
              >
                <FontAwesomeIcon icon={faAngleLeft} /> Prev
              </span>
            </div>
          ) : (
            <div className="pager">
              <Link to={prevPageUrl} data-testid="previousPageButton">
                <FontAwesomeIcon icon={faAngleLeft} /> Prev
              </Link>
            </div>
          )}
        </li>
        <li className="list-inline-item mx-2">
          <span>
            <span data-testid="pageInterval">Page {startEnd}</span>
            &nbsp;of&nbsp;
            <span data-testid="totalPages">{lastPage}</span>
          </span>
        </li>
        <li className="list-inline-item ml-2">
          {total === "..." || end < total ? (
            <div className="pager">
              <Link
                data-testid="nextPageButton"
                to={`${pathname}?${nextUrlParams}`}
              >
                Next <FontAwesomeIcon icon={faAngleRight} />
              </Link>
            </div>
          ) : (
            <div className="pager">
              <span className="text-muted" data-testid="disabledNextPageButton">
                Next <FontAwesomeIcon icon={faAngleRight} />
              </span>
            </div>
          )}
        </li>

        <li className="list-inline-item">
          <div className="pager">
            {!isNaN(lastPage) && end < total ? (
              <Link
                data-testid="nextPageButton"
                to={`${pathname}?${lastUrlParams}`}
              >
                Last <FontAwesomeIcon icon={faAngleDoubleRight} />
              </Link>
            ) : (
              <span className="text-muted">
                Last <FontAwesomeIcon icon={faAngleDoubleRight} />
              </span>
            )}
          </div>
        </li>
      </ul>
    </div>
  ) : null;
};

export default Pager;
